import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { IEmployee, IUserSession } from '../../../../@types/employee';
import { connect } from 'react-redux';
import { IRootState } from '../../../../@types/redux';
import ProjectDialog from '../../../customComponentV2/dialog/ProjectDialog';
import DateHelper from '../../../../services/helper/dateHelper';
import { booleanToYesNo } from '../../../../services/helper/functionHelper';
import ConfirmationDialog from '../../../customComponentV2/dialog/ConfirmationDialog';
import CustomTable from '../../../customComponentV2/DataGrid/CustomTable';
import Screen from '../../../customComponentV2/screen/Screen';
import { Card } from '@material-ui/core';
import { IGroundWaterZone } from '../../../../@types/model/borehole/groundWaterZone/groundWaterZone';
import { IGroundWaterZoneFormValues } from '../../../../@types/model/borehole/groundWaterZone/groundWaterZoneFormValues';
import GroundWaterZoneModelHelper from '../../../../@types/model/borehole/groundWaterZone/groundWaterZoneModelHelper';
import { useGetGroundWaterZones, useHandleDeleteGroundWaterZone, useHandleUpsertGroundWaterZone } from '../../../../hooks/query/borehole/masterData/groundWaterZoneQueries';
import { UpsertTypeEnum } from '../../../../@types/enum/upsertType';
import GroundWaterZoneEditForm from './GroundWaterZoneEditForm';

interface IGroundWaterZoneManagerProps {
    session ?: IUserSession | null;
}

const GroundWaterZoneManagerComponent = (props : IGroundWaterZoneManagerProps) : React.ReactElement => {
    const { isLoading: isLoadingGroundWaterZones, data: groundWaterZones } = useGetGroundWaterZones();
    const { isLoading: isUpsertingGroundWaterZone, mutate: upsertGroundWaterZone, isSuccess: isGroundWaterZoneUpsertSuccessful } = useHandleUpsertGroundWaterZone();
    const { isLoading: isDeletingGroundWaterZone, mutate: deleteGroundWaterZone, isSuccess: isGroundWaterZoneDeleteSuccessful } = useHandleDeleteGroundWaterZone();

    const [isEditDialogOpen, setIsEditDialogOpen] = useState<boolean>(false);
    const [selectedGroundWaterZone, setSelectedGroundWaterZone] = useState<IGroundWaterZone | null>(null);
    const [deletingGroundWaterZone, setDeletingGroundWaterZone] = useState<IGroundWaterZone | null>(null);

    /*================================================================================================================
     *                                                  Effects
     * ==============================================================================================================*/

    useEffect(() => {
        if (isGroundWaterZoneUpsertSuccessful) {
            onModalDismiss();
        }
    }, [isGroundWaterZoneUpsertSuccessful]);

    useEffect(() => {
        if (isGroundWaterZoneDeleteSuccessful) {
            closeDeletePopup();
        }
    }, [isGroundWaterZoneDeleteSuccessful]);

    /*================================================================================================================
     *                                                  Async Methods
     * ==============================================================================================================*/

    /*================================================================================================================
     *                                                  Handler Methods
     * ==============================================================================================================*/

    const edit = (selectedGroundWaterZone : IGroundWaterZone) : void => {
        const groundWaterZone = groundWaterZones?.find(x => x.id === selectedGroundWaterZone.id) || null;
        setIsEditDialogOpen(true);
        setSelectedGroundWaterZone(groundWaterZone);
    };

    const openModal = () : void => {
        setIsEditDialogOpen(true);
    };

    const onModalDismiss = () : void => {
        setIsEditDialogOpen(false);
        setSelectedGroundWaterZone(null);
    };

    const onDelete = (selectedGroundWaterZone : IGroundWaterZone) : void => {
        const groundWaterZone = groundWaterZones?.find(x => x.id === selectedGroundWaterZone.id) || null;
        setDeletingGroundWaterZone(groundWaterZone);
    };

    const closeDeletePopup = () : void => {
        setDeletingGroundWaterZone(null);
    };

    const onDeleteGroundWaterZone = () : void => {
        if (deletingGroundWaterZone) {
            deleteGroundWaterZone(deletingGroundWaterZone.id);
        }
    };

    const onSubmit = (values : IGroundWaterZoneFormValues) : void => {
        const upsert = GroundWaterZoneModelHelper.createUpsert(values);

        upsertGroundWaterZone({
            upsert,
            type: upsert.id == 0 ? UpsertTypeEnum.Add : UpsertTypeEnum.Edit
        });
    };

    /*================================================================================================================
     *                                                  Memos
     * ==============================================================================================================*/

    const getInitialFormValues = useMemo<IGroundWaterZoneFormValues>(() => {
        return GroundWaterZoneModelHelper.createFormValues(selectedGroundWaterZone);
    }, [selectedGroundWaterZone]);

    const isLoading = useMemo<boolean>(() => {
        return isLoadingGroundWaterZones 
        || isUpsertingGroundWaterZone 
        || isDeletingGroundWaterZone
        || isLoadingGroundWaterZones;

    }, [isLoadingGroundWaterZones, 
        isUpsertingGroundWaterZone, 
        isDeletingGroundWaterZone,
        isLoadingGroundWaterZones
    ]);

    /*================================================================================================================
     *                                                  Render Methods
     * ==============================================================================================================*/

    return (
        <Screen
            useAnimatedLoader
            isLoading={isLoading}
            isScrollable={false}>
            <Card className='fdc'>
                <CustomTable<IGroundWaterZone>
                    enableAdding
                    addFunction={openModal}
                    disableAddButton={!props.session?.employee.IsBoreholeMasterDataAdmin || !props.session?.employee.IsBoreholeAdmin}

                    enableEditing
                    editFunction={edit}
                    disableEditButton={!props.session?.employee.IsBoreholeMasterDataAdmin || !props.session?.employee.IsBoreholeAdmin}

                    enableDeleting
                    deleteFunction={onDelete}
                    disableDeleteButton={(row : IGroundWaterZone) : boolean => (!props.session?.employee.IsBoreholeMasterDataAdmin && !props.session?.employee.IsBoreholeAdmin) || !row.isActive}

                    enableSorting
                    enableFiltering
                    fitWidthToPage
                    columns={[
                        { title: 'Code', field: 'code', enableFiltering: true, enableSorting: true },
                        { title: 'Name', field: 'name', enableFiltering: true, enableSorting: true },
                        { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                        { title: 'Created On', field: 'createdOn', formatFunction: DateHelper.formatDateUtc, type: 'date', enableFiltering: true, enableSorting: true },
                        { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                        { title: 'Updated On', field: 'updatedOn', formatFunction: DateHelper.formatDateUtc, type: 'date', enableFiltering: true, enableSorting: true },
                        { title: 'Active?', field: 'isActive', formatFunction: booleanToYesNo, type: 'boolean', enableFiltering: true, enableSorting: true },
                    ]}
                    rows={groundWaterZones ?? []}
                    initialSortOrder={[{ columnName: 'createdOn_Created On', direction: 'desc' }]}
                    isActive={(row : IGroundWaterZone) : boolean => row.isActive}
                />
                <ProjectDialog
                    title={selectedGroundWaterZone ? 'Edit Ground Water Zone' : 'Add Ground Water Zone'}
                    isLoadingCircular={isLoading}
                    isOpen={isEditDialogOpen}
                    fullWidth
                    maxWidth={'sm'}
                    onClose={onModalDismiss}>
                    <GroundWaterZoneEditForm initialValues={getInitialFormValues} onSubmit={onSubmit} onClose={onModalDismiss}/>
                </ProjectDialog >
                <ConfirmationDialog
                    isLoading={isLoading}
                    open={!!deletingGroundWaterZone}
                    title={'Inactivate Ground water zone'}
                    description={'Are you sure you would like to set the following item to inactive? '}
                    onAccept={onDeleteGroundWaterZone}
                    onClose={closeDeletePopup}
                    dialogType='red'
                />
            </Card>
        </Screen>
    );
};

const mapStateToProps = (state : IRootState) => {
    return {
        session: state.auth.session,
    };
};

export default connect(
    mapStateToProps,
)(GroundWaterZoneManagerComponent);